/**
 * RRR class picker: per-lesson location filter interaction, plus a
 * UX-only "disable Add to cart until valid" polish matching the pattern
 * used elsewhere in this theme.
 *
 * Each lesson has its OWN independent location filter (when that lesson's
 * slots span 2+ locations) - filtering Lesson 1 to "Online" has no effect
 * on Lesson 2's dropdown. This matches a subject that runs different
 * lessons at different venues, where a customer might mix locations
 * across lessons.
 *
 * Every <select> in this block is wired to the product form via the HTML
 * `form` attribute (see rrr-class-picker.liquid) and is natively required,
 * so the browser already blocks invalid submission on its own - this
 * script never intercepts the actual add-to-cart request.
 */
class RrrClassPicker extends HTMLElement {
  connectedCallback() {
    this.#bindLessonLocationFilters();

    const formId = this.dataset.formId;
    const form = formId ? document.getElementById(formId) : null;
    if (!(form instanceof HTMLFormElement)) return;

    this.#syncButtonState(form);

    if (form.dataset.rrrSyncAttached === 'true') return;
    form.dataset.rrrSyncAttached = 'true';

    // Listens on the whole section, not the form itself: these selects
    // (and student-details' inputs) are associated with the form via the
    // `form="..."` attribute rather than DOM nesting, so their 'input'/
    // 'change' events bubble up through their REAL DOM ancestors (this
    // section) and never reach the <form> element at all. Without this,
    // the button only ever gets checked once, at page load, and never
    // again - looking permanently disabled even once every field is
    // filled in.
    const sync = () => this.#syncButtonState(form);
    const scope = form.closest('.product-information, .shopify-section') || document;
    scope.addEventListener('input', sync);
    scope.addEventListener('change', sync);
  }

  #bindLessonLocationFilters() {
    this.querySelectorAll('[data-rrr-lesson-location-filter]').forEach((filter) => {
      if (!(filter instanceof HTMLSelectElement)) return;

      const field = filter.closest('.rrr-class-picker__field');
      const dropdown = field?.querySelector('[data-rrr-dropdown]');
      if (!(dropdown instanceof HTMLSelectElement)) return;

      filter.addEventListener('change', () => {
        this.#applyLocationFilter(dropdown, filter.value);
      });
    });
  }

  /**
   * Hides options that don't match the chosen location on ONE lesson's
   * dropdown only. Options with no location set (location-agnostic)
   * always stay visible. Clears the selection if it no longer matches so
   * the customer has to re-pick within the filtered set.
   * @param {HTMLSelectElement} dropdown - this lesson's own <select>
   * @param {string} location - empty string means "all locations"
   */
  #applyLocationFilter(dropdown, location) {
    let selectedOptionHidden = false;

    [...dropdown.options].forEach((option) => {
      if (!option.value) return; // the placeholder option
      const optionLocation = option.dataset.rrrLocation || '';
      const matches = location === '' || optionLocation === '' || optionLocation === location;
      // Both hidden AND disabled: iOS Safari's native picker wheel does
      // not reliably respect `hidden` alone on <option> elements - a
      // "hidden" option can still show up and be selectable there.
      // `disabled` is honored consistently across browsers.
      option.hidden = !matches;
      option.disabled = !matches;
      if (!matches && option.selected) selectedOptionHidden = true;
    });

    if (selectedOptionHidden) dropdown.value = '';

    const form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    if (form instanceof HTMLFormElement) this.#syncButtonState(form);
  }

  /**
   * Scoped to the whole product-information section (not just
   * product-form-component) so this reaches BOTH the inline buy-buttons
   * button AND the mobile sticky-add-to-cart bar's button - the sticky
   * bar renders as a sibling of product-form-component, not a descendant,
   * so a narrower query silently misses it and leaves it stuck disabled
   * on mobile even once the form is valid.
   * @param {HTMLFormElement} form
   */
  #syncButtonState(form) {
    const ready = form.checkValidity();
    const scope = form.closest('.product-information, .shopify-section') || document;
    const addButtons = scope.querySelectorAll('[ref="addToCartButton"], .add-to-cart-button');

    addButtons?.forEach((btn) => {
      if (btn instanceof HTMLButtonElement) btn.disabled = !ready;
    });
  }
}

if (!customElements.get('rrr-class-picker')) {
  customElements.define('rrr-class-picker', RrrClassPicker);
}
